import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Search, Pencil, Save, X, Trash2, Loader2, Plus, History } from "lucide-react";
import { toast } from "sonner";

export default function YarnMasterPage() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [dragging, setDragging] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ count: "", quality: "", price_per_unit: "", sizing_charge_per_unit: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLabel, setHistoryLabel] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try { const res = await api.get("/yarn-master"); setData(res.data); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (file) => {
    if (!file || !file.name.endsWith(".csv")) { toast.error("Please upload a CSV file"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/yarn-master/upload-csv", fd);
      toast.success(res.data.message); fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); handleUpload(e.dataTransfer.files[0]); };

  const handleAdd = async () => {
    if (!addForm.count || !addForm.quality) { toast.error("Count and Quality are required"); return; }
    setAddLoading(true);
    try {
      await api.post("/yarn-master", {
        count: addForm.count, quality: addForm.quality,
        price_per_unit: parseFloat(addForm.price_per_unit) || 0,
        sizing_charge_per_unit: parseFloat(addForm.sizing_charge_per_unit) || 0,
      });
      toast.success("Yarn added"); setAddOpen(false);
      setAddForm({ count: "", quality: "", price_per_unit: "", sizing_charge_per_unit: "" });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAddLoading(false); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValues({ price_per_unit: item.price_per_unit, sizing_charge_per_unit: item.sizing_charge_per_unit });
  };

  const saveEdit = async (id) => {
    try { await api.put(`/yarn-master/${id}`, editValues); toast.success("Price updated"); setEditingId(null); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteItem = async (id) => {
    try { await api.delete(`/yarn-master/${id}`); toast.success("Deleted"); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const viewHistory = async (item) => {
    setHistoryLabel(`${item.count} / ${item.quality}`);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/yarn-master/price-history?count=${encodeURIComponent(item.count)}&quality=${encodeURIComponent(item.quality)}`);
      setHistoryData(res.data);
    } catch (err) { toast.error(formatApiError(err)); setHistoryData([]); }
    finally { setHistoryLoading(false); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

  const filtered = data.filter((item) =>
    item.count?.toLowerCase().includes(search.toLowerCase()) || item.quality?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="yarn-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Yarn Price Master</h1>
          <p className="text-sm text-stone-500 mt-1">Manage yarn pricing. Upload CSV to merge/update or add manually.</p>
        </div>
        <Button data-testid="add-yarn-button" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />Add Yarn
        </Button>
      </div>

      <div className={`csv-dropzone ${dragging ? "dragging" : ""}`} data-testid="csv-upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={handleDrop} onClick={() => fileRef.current?.click()}>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="csv-file-input" onChange={(e) => handleUpload(e.target.files[0])} />
        {uploading ? <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#C2410C]" /> : (
          <><Upload className="h-8 w-8 mx-auto text-stone-400 mb-2" strokeWidth={1.5} />
            <p className="text-sm font-medium text-stone-700">Drop CSV to merge/update existing data</p>
            <p className="text-xs text-stone-400 mt-1">Columns: Count, Quality, Price_Per_Unit, Sizing_Charge_Per_Unit</p></>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <Input data-testid="yarn-search-input" placeholder="Search by count or quality..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" />
      </div>

      <div className="bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Count</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Quality</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Price/Unit</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Sizing/Unit</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 w-36">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-400">{data.length === 0 ? "No data. Upload CSV or add manually." : "No matching records."}</TableCell></TableRow>
            ) : filtered.map((item, idx) => (
              <TableRow key={item.id} className="animate-row hover:bg-stone-50/50" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`yarn-row-${item.id}`}>
                <TableCell className="px-4 py-2.5 text-sm font-medium text-stone-900">{item.count}</TableCell>
                <TableCell className="px-4 py-2.5 text-sm text-stone-700">{item.quality}</TableCell>
                <TableCell className="px-4 py-2.5 text-sm text-right">
                  {editingId === item.id ? <Input type="number" step="0.01" data-testid="edit-price-input" value={editValues.price_per_unit} onChange={(e) => setEditValues((v) => ({ ...v, price_per_unit: parseFloat(e.target.value) || 0 }))} className="h-8 w-28 text-right ml-auto" />
                  : <span className="font-mono">{item.price_per_unit?.toFixed(2)}</span>}
                </TableCell>
                <TableCell className="px-4 py-2.5 text-sm text-right">
                  {editingId === item.id ? <Input type="number" step="0.01" data-testid="edit-sizing-input" value={editValues.sizing_charge_per_unit} onChange={(e) => setEditValues((v) => ({ ...v, sizing_charge_per_unit: parseFloat(e.target.value) || 0 }))} className="h-8 w-28 text-right ml-auto" />
                  : <span className="font-mono">{item.sizing_charge_per_unit?.toFixed(2)}</span>}
                </TableCell>
                <TableCell className="px-4 py-2.5">
                  {editingId === item.id ? (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" data-testid="save-edit-button" onClick={() => saveEdit(item.id)} className="h-7 w-7 text-green-600 hover:bg-green-50"><Save className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setEditingId(null)} className="h-7 w-7 text-stone-500 hover:bg-stone-100"><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" data-testid="edit-yarn-button" onClick={() => startEdit(item)} className="h-7 w-7 text-stone-500 hover:text-[#C2410C] hover:bg-orange-50"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" data-testid="history-yarn-button" onClick={() => viewHistory(item)} className="h-7 w-7 text-stone-500 hover:text-blue-600 hover:bg-blue-50"><History className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" data-testid="delete-yarn-button" onClick={() => deleteItem(item.id)} className="h-7 w-7 text-stone-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data.length > 0 && <p className="text-xs text-stone-400">Showing {filtered.length} of {data.length} records</p>}

      {/* Add Yarn Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Yarn</DialogTitle>
            <DialogDescription>Enter the yarn count, quality, and pricing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-sm font-medium mb-1.5 block">Count *</Label><Input data-testid="add-count-input" placeholder="e.g. 40" value={addForm.count} onChange={(e) => setAddForm((f) => ({ ...f, count: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Quality *</Label><Input data-testid="add-quality-input" placeholder="e.g. Combed" value={addForm.quality} onChange={(e) => setAddForm((f) => ({ ...f, quality: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Price Per Unit</Label><Input data-testid="add-price-input" type="number" step="0.01" value={addForm.price_per_unit} onChange={(e) => setAddForm((f) => ({ ...f, price_per_unit: e.target.value }))} /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Sizing Charge Per Unit</Label><Input data-testid="add-sizing-input" type="number" step="0.01" value={addForm.sizing_charge_per_unit} onChange={(e) => setAddForm((f) => ({ ...f, sizing_charge_per_unit: e.target.value }))} /></div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="add-yarn-submit" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={handleAdd} disabled={addLoading}>
              {addLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{addLoading ? "Adding..." : "Add Yarn"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Price History — {historyLabel}</DialogTitle>
            <DialogDescription>Historical price changes for this yarn.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" /></div>
          ) : historyData.length === 0 ? (
            <p className="py-8 text-center text-sm text-stone-400">No price changes recorded yet.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="text-xs font-semibold uppercase text-stone-500 px-3 py-2">Date</TableHead>
                    <TableHead className="text-xs font-semibold uppercase text-stone-500 px-3 py-2 text-right">Old Price</TableHead>
                    <TableHead className="text-xs font-semibold uppercase text-stone-500 px-3 py-2 text-right">New Price</TableHead>
                    <TableHead className="text-xs font-semibold uppercase text-stone-500 px-3 py-2 text-right">Old Sizing</TableHead>
                    <TableHead className="text-xs font-semibold uppercase text-stone-500 px-3 py-2 text-right">New Sizing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyData.map((h, i) => (
                    <TableRow key={h.id || i}>
                      <TableCell className="px-3 py-2 text-xs text-stone-600">{formatDate(h.changed_at)}</TableCell>
                      <TableCell className="px-3 py-2 text-xs text-right font-mono">{h.old_price_per_unit?.toFixed(2)}</TableCell>
                      <TableCell className="px-3 py-2 text-xs text-right font-mono font-semibold">{h.new_price_per_unit?.toFixed(2)}</TableCell>
                      <TableCell className="px-3 py-2 text-xs text-right font-mono">{h.old_sizing_charge?.toFixed(2)}</TableCell>
                      <TableCell className="px-3 py-2 text-xs text-right font-mono font-semibold">{h.new_sizing_charge?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
